import type Alpine from 'alpinejs';
import {
    requestWithAlpine,
    setNavigateResponseHandler,
    type NavigationHistory,
    type RequestOptions,
    type RequestResult,
} from './request';
import {
    setEffectRequestFilter,
    setMorphExclusionFilter,
    type StreamAlpine,
} from './stream';
import {isRecord, warn} from '../../utils';

type ScrollPosition = {x: number; y: number};
type NavigationOptions = RequestOptions & {history?: NavigationHistory};
type ScrollState = {
    containers: Record<string, ScrollPosition>;
    window: ScrollPosition;
};
type NavigateHistoryState = {
    id: string;
    index: number;
    scroll: ScrollState;
    url: string;
};
type TrellisHistoryState = {
    navigate: NavigateHistoryState;
};
type NavigationDetail = {
    history: boolean;
    source: Element | Document;
    url: URL;
};
type NavigationSession = {
    controller: AbortController;
    destination: URL;
    entryId: string | null;
    history: boolean;
    disposition: NavigationHistory;
    requestId: string;
    requestOptions?: RequestOptions;
    source?: Element;
};

const HISTORY_NAMESPACE = '__trellis';
const SCROLL_CONTAINER_SELECTOR = '[data-ui-scroll-restoration]';
const COMPLETED_SESSION_LIMIT = 20;
const CANCELLED_REQUEST_LIMIT = 100;

let registered = false;
let activeSession: NavigationSession | null = null;
let currentIndex = 0;
let ignoreNextPopstate = false;
let scrollSaveFrame: number | null = null;
const scrollSnapshots = new Map<string, ScrollState>();
const cancelledRequestIds = new Set<string>();
const completedSessions = new Map<string, NavigationSession>();

const navigationId = (): string => crypto.randomUUID();

const emptyScrollState = (): ScrollState => ({
    containers: {},
    window: {x: 0, y: 0},
});

const scrollContainerKey = (element: Element): string | null => {
    const declared = element.getAttribute('data-ui-scroll-restoration')?.trim();
    if (declared) return declared;
    return element.id || null;
};

const captureScroll = (): ScrollState => {
    const containers: Record<string, ScrollPosition> = {};
    document.querySelectorAll<HTMLElement>(SCROLL_CONTAINER_SELECTOR).forEach((element) => {
        const key = scrollContainerKey(element);
        if (!key) return;
        containers[key] = {x: element.scrollLeft, y: element.scrollTop};
    });
    return {
        containers,
        window: {x: window.scrollX, y: window.scrollY},
    };
};

const navigateStateFrom = (state: unknown): NavigateHistoryState | null => {
    if (!isRecord(state)) return null;
    const trellis = state[HISTORY_NAMESPACE];
    if (!isRecord(trellis)) return null;
    const navigate = trellis.navigate;
    if (!isRecord(navigate)) return null;
    if (
        typeof navigate.id !== 'string'
        || typeof navigate.index !== 'number'
        || typeof navigate.url !== 'string'
        || !isRecord(navigate.scroll)
    ) return null;
    return navigate as NavigateHistoryState;
};

const mergedHistoryState = (navigate: NavigateHistoryState): Record<string, unknown> => {
    const state = isRecord(window.history.state) ? window.history.state : {};
    const namespace = isRecord(state[HISTORY_NAMESPACE])
        ? state[HISTORY_NAMESPACE] as Record<string, unknown>
        : {};
    return {
        ...state,
        [HISTORY_NAMESPACE]: {
            ...namespace,
            navigate,
        } satisfies TrellisHistoryState,
    };
};

const replaceEntry = (navigate: NavigateHistoryState, url: string | URL = navigate.url): void => {
    window.history.replaceState(mergedHistoryState(navigate), '', url);
};

const saveCurrentScroll = (): void => {
    const navigate = navigateStateFrom(window.history.state);
    if (!navigate) return;
    const scroll = captureScroll();
    scrollSnapshots.set(navigate.id, scroll);
    replaceEntry({...navigate, scroll});
};

const flushCurrentScroll = (): void => {
    if (scrollSaveFrame !== null) {
        cancelAnimationFrame(scrollSaveFrame);
        scrollSaveFrame = null;
    }
    saveCurrentScroll();
};

const scheduleCurrentScroll = (): void => {
    const navigate = navigateStateFrom(window.history.state);
    if (!navigate) return;
    scrollSnapshots.set(navigate.id, captureScroll());
    if (scrollSaveFrame !== null) return;
    scrollSaveFrame = requestAnimationFrame(() => {
        scrollSaveFrame = null;
        const current = navigateStateFrom(window.history.state);
        if (!current) return;
        const scroll = scrollSnapshots.get(current.id);
        if (scroll) replaceEntry({...current, scroll});
    });
};

const restoreScroll = (scroll: ScrollState): void => {
    requestAnimationFrame(() => {
        window.scrollTo({left: scroll.window.x, top: scroll.window.y, behavior: 'instant'});
        document.querySelectorAll<HTMLElement>(SCROLL_CONTAINER_SELECTOR).forEach((element) => {
            const key = scrollContainerKey(element);
            const position = key ? scroll.containers[key] : undefined;
            element.scrollTo({
                left: position?.x ?? 0,
                top: position?.y ?? 0,
                behavior: 'instant',
            });
        });
    });
};

const dispatchNavigation = (
    name: string,
    session: Pick<NavigationSession, 'destination' | 'history' | 'source'>,
    cancelable = false,
): boolean => {
    const source = session.source?.isConnected ? session.source : document;
    return source.dispatchEvent(new CustomEvent<NavigationDetail>(name, {
        bubbles: true,
        cancelable,
        composed: true,
        detail: {
            history: session.history,
            source,
            url: session.destination,
        },
    }));
};

const rememberCompleted = (session: NavigationSession): void => {
    completedSessions.set(session.requestId, session);
    while (completedSessions.size > COMPLETED_SESSION_LIMIT) {
        const oldest = completedSessions.keys().next().value as string | undefined;
        if (!oldest) break;
        completedSessions.delete(oldest);
    }
};

const rejectRequestId = (requestId: string): void => {
    cancelledRequestIds.add(requestId);
    while (cancelledRequestIds.size > CANCELLED_REQUEST_LIMIT) {
        const oldest = cancelledRequestIds.values().next().value as string | undefined;
        if (!oldest) break;
        cancelledRequestIds.delete(oldest);
    }
};

const cancelActive = (): void => {
    if (!activeSession) return;
    rejectRequestId(activeSession.requestId);
    activeSession.controller.abort('Superseded by a newer Trellis navigation');
    activeSession = null;
};

const supersedePriorNavigations = (): void => {
    cancelActive();
    completedSessions.forEach((session) => rejectRequestId(session.requestId));
    completedSessions.clear();
};

const nativeNavigate = (url: URL): void => {
    window.location.assign(url.href);
};

const internalUrl = (value: string): URL | null => {
    const url = new URL(value, document.baseURI);
    return url.origin === window.location.origin ? url : null;
};

const pushDestination = (session: NavigationSession): NavigateHistoryState => {
    flushCurrentScroll();
    const navigate: NavigateHistoryState = {
        id: navigationId(),
        index: currentIndex + 1,
        scroll: emptyScrollState(),
        url: session.destination.href,
    };
    currentIndex = navigate.index;
    window.history.pushState(mergedHistoryState(navigate), '', session.destination);
    session.entryId = navigate.id;
    return navigate;
};

const replaceDestination = (session: NavigationSession): NavigateHistoryState => {
    const navigate: NavigateHistoryState = {
        id: navigationId(),
        index: currentIndex,
        scroll: emptyScrollState(),
        url: session.destination.href,
    };
    replaceEntry(navigate, session.destination);
    session.entryId = navigate.id;
    return navigate;
};

const commitsWithReplace = (session: NavigationSession): boolean => {
    if (session.disposition === 'replace') return true;
    if (session.disposition === 'push') return false;
    const current = new URL(window.location.href);
    return current.origin === session.destination.origin && current.pathname === session.destination.pathname;
};

const fallbackFor = (session: NavigationSession, result?: RequestResult): void => {
    if (
        result?.type === 'aborted'
        || result?.type === 'cancelled'
        || result?.type === 'redirect'
    ) return;
    nativeNavigate(session.destination);
};

const finishSession = (
    session: NavigationSession,
    result: RequestResult,
    restoredState?: NavigateHistoryState,
): void => {
    if (cancelledRequestIds.has(session.requestId)) return;
    if (result.type !== 'empty') {
        fallbackFor(session, result);
        return;
    }

    const destinationState = restoredState ?? (
        commitsWithReplace(session) ? replaceDestination(session) : pushDestination(session)
    );
    if (restoredState && session.destination.href !== restoredState.url) {
        const redirected = {...restoredState, url: session.destination.href};
        replaceEntry(redirected, session.destination);
        session.entryId = redirected.id;
    }
    restoreScroll(destinationState.scroll);
    dispatchNavigation('ui:navigated', session);
};

const performNavigation = (
    AlpineInstance: StreamAlpine,
    destination: URL,
    options: {
        history: boolean;
        disposition?: NavigationHistory;
        preflight?: boolean;
        requestOptions?: RequestOptions;
        source?: Element;
        restoredState?: NavigateHistoryState;
    },
): Promise<void> => {
    const session: NavigationSession = {
        controller: new AbortController(),
        destination,
        entryId: options.restoredState?.id ?? null,
        history: options.history,
        disposition: options.disposition ?? 'auto',
        requestId: navigationId(),
        ...(options.requestOptions ? {requestOptions: options.requestOptions} : {}),
        ...(options.source ? {source: options.source} : {}),
    };

    if (options.preflight !== false && !dispatchNavigation('ui:navigate', session, true)) return Promise.resolve();
    supersedePriorNavigations();
    activeSession = session;
    dispatchNavigation('ui:navigating', session);

    const headers = new Headers(session.requestOptions?.headers);
    headers.set('X-Trellis-Navigation', session.history ? 'restore' : 'navigate');
    let initialRequest = true;
    const requestDestination = async (): Promise<RequestResult> => {
        const requestOptions = initialRequest ? session.requestOptions : undefined;
        const source = initialRequest ? session.source : undefined;
        initialRequest = false;
        const result = await requestWithAlpine(AlpineInstance, session.destination, {
            ...requestOptions,
            headers,
            method: 'GET',
            requestId: session.requestId,
            signal: session.controller.signal,
            ...(source ? {source} : {}),
        });
        session.destination = new URL(result.url, document.baseURI);
        if (!result.navigation) return result;

        const redirected = internalUrl(result.navigation.url);
        if (!redirected) {
            nativeNavigate(new URL(result.navigation.url, document.baseURI));
            return {...result, type: 'redirect'};
        }
        session.destination = redirected;
        if (result.navigation.history !== 'auto') session.disposition = result.navigation.history;
        return requestDestination();
    };

    return requestDestination().then((result) => {
        if (activeSession?.requestId !== session.requestId && !completedSessions.has(session.requestId)) return;
        finishSession(session, result, options.restoredState);
    }).catch((error: unknown) => {
        if (!session.controller.signal.aborted) {
            warn('x-ui-request.navigate: restoration failed', error);
            fallbackFor(session);
        }
    }).finally(() => {
        if (activeSession?.requestId === session.requestId) activeSession = null;
        if (!cancelledRequestIds.has(session.requestId)) rememberCompleted(session);
    });
};

const initializeHistory = (): void => {
    const existing = navigateStateFrom(window.history.state);
    if (existing) {
        currentIndex = existing.index;
    } else {
        const navigate: NavigateHistoryState = {
            id: navigationId(),
            index: 0,
            scroll: captureScroll(),
            url: window.location.href,
        };
        replaceEntry(navigate);
    }
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
};

const registerNavigationRuntime = (AlpineInstance: Alpine.Alpine | StreamAlpine): void => {
    if (registered) return;
    registered = true;
    initializeHistory();
    setEffectRequestFilter((requestId) => !requestId || !cancelledRequestIds.has(requestId));
    setMorphExclusionFilter((requestId, element) => (
        element.matches('[data-ui-stream]')
        && Boolean(
            requestId
            && (
                activeSession?.requestId === requestId
                || completedSessions.has(requestId)
            )
        )
    ));
    setNavigateResponseHandler((result) => {
        if (!result.navigation) return false;
        if (cancelledRequestIds.has(result.requestId)) return true;
        if (activeSession?.requestId === result.requestId) return true;
        const destination = internalUrl(result.navigation.url);
        if (!destination) {
            nativeNavigate(new URL(result.navigation.url, document.baseURI));
            return true;
        }
        performNavigation(AlpineInstance as StreamAlpine, destination, {
            history: false,
            disposition: result.navigation.history,
        });
        return true;
    });

    window.addEventListener('scroll', scheduleCurrentScroll, {passive: true});
    document.addEventListener('scroll', scheduleCurrentScroll, {capture: true, passive: true});
    window.addEventListener('pagehide', flushCurrentScroll);
    window.addEventListener('popstate', (event) => {
        if (ignoreNextPopstate) {
            ignoreNextPopstate = false;
            return;
        }
        const restoredState = navigateStateFrom(event.state);
        if (!restoredState) {
            nativeNavigate(new URL(window.location.href));
            return;
        }
        const restoredScroll = scrollSnapshots.get(restoredState.id);
        const effectiveState = restoredScroll
            ? {...restoredState, scroll: restoredScroll}
            : restoredState;
        const destination = internalUrl(restoredState.url);
        if (!destination || destination.href !== window.location.href) {
            nativeNavigate(new URL(window.location.href));
            return;
        }

        const previousIndex = currentIndex;
        currentIndex = restoredState.index;
        const probe: NavigationSession = {
            controller: new AbortController(),
            destination,
            entryId: restoredState.id,
            history: true,
            disposition: 'replace',
            requestId: navigationId(),
        };
        if (!dispatchNavigation('ui:navigate', probe, true)) {
            ignoreNextPopstate = true;
            window.history.go(previousIndex - restoredState.index);
            currentIndex = previousIndex;
            return;
        }
        performNavigation(AlpineInstance as StreamAlpine, destination, {
            history: true,
            preflight: false,
            restoredState: effectiveState,
        });
    });
};

const navigateWithAlpine = (
    AlpineInstance: Alpine.Alpine | StreamAlpine,
    url: string | URL,
    source?: Element,
    options?: NavigationOptions,
): Promise<void> => {
    const {history = 'auto', ...requestOptions} = options ?? {};
    return performNavigation(AlpineInstance as StreamAlpine, new URL(url, document.baseURI), {
        history: false,
        disposition: history,
        requestOptions,
        ...(source ? {source} : {}),
    });
};

export {
    HISTORY_NAMESPACE,
    SCROLL_CONTAINER_SELECTOR,
    navigateWithAlpine,
    registerNavigationRuntime,
};
export type {NavigationOptions};
